// Contenido del plan de implementación Enterprise. Es el mismo checklist del
// artifact de kickoff: el documento se entrega al cliente en español, así que
// estos textos no pasan por i18n — sólo la interfaz alrededor.

export const PARTIDAS = {
  mp: {
    label: 'Versión anterior de MP', desc: 'Ya usaban nuestro sistema anterior',
    qs: [
      '¿Qué módulos usaban en la versión anterior?',
      '¿Cuántos activos, preventivos y usuarios tienen registrados?',
      '¿Seguían registrando órdenes de trabajo hasta hoy?',
      '¿Su información está al día o hay que depurarla?',
      '¿Qué sí les funcionaba y qué no?',
    ],
    tareas: ['Exportar la información de la versión anterior', 'Depurar activos, preventivos y usuarios'],
    haremos: 'Migrar su información a MPTotal y depurar lo que ya no sirve.',
  },
  excel: {
    label: 'Excel', desc: 'Llevan su control en hojas de cálculo',
    qs: [
      '¿Nos compartieron el archivo? ¿Cómo está organizado?',
      '¿Cómo es su proceso actual?',
      '¿Quién lo llena y cada cuándo?',
      '¿Qué registran: activos, preventivos, órdenes, refacciones?',
      '¿Cómo se asigna y se cierra un trabajo?',
    ],
    tareas: ['Recibir los archivos de Excel', 'Pasar la información a las plantillas de importación'],
    haremos: 'Pasar su archivo a nuestras plantillas de importación.',
  },
  plataforma: {
    label: 'Otra plataforma', desc: 'Vienen de otro software de mantenimiento',
    qs: [
      '¿Qué plataforma usaban?',
      '¿Cuál era su proceso en ella?',
      '¿Qué les funcionaba y por qué la dejan?',
      '¿Pueden exportar su información a Excel o CSV?',
      '¿Hasta qué fecha tienen acceso?',
    ],
    tareas: ['Recibir la exportación de la plataforma anterior', 'Relacionar sus campos con los de MPTotal'],
    haremos: 'Revisar su exportación y migrar lo que sirve para operar.',
  },
  cero: {
    label: 'No tienen nada', desc: 'Empezamos desde cero',
    qs: [
      '¿Cómo se entera mantenimiento de una falla?',
      '¿Quién decide qué se repara primero?',
      '¿Qué equipos son críticos para la operación?',
      '¿Qué preventivos pide el fabricante o alguna norma?',
      '¿Tienen lista de refacciones?',
    ],
    tareas: ['Recorrido para levantar los equipos críticos', 'Definir el proceso base: solicitud, orden y cierre'],
    haremos: 'Levantar sus equipos críticos y proponer un proceso base.',
  },
};

export const MODULOS = [
  { id: 'preventivo', label: 'Mantenimiento preventivo' },
  { id: 'correctivo', label: 'Mantenimiento correctivo' },
  { id: 'inventario', label: 'Inventario y herramientas' },
  { id: 'integraciones', label: 'Integraciones (ERP, compras)' },
];

export const MODULE_CHOICES = [['f1', 'Fase 1'], ['f2', 'Después'], ['na', 'No aplica']];

export const ROLES = [
  { id: 'admin', label: 'Administrador / planificador', acceso: 'Toda la plataforma', hace: 'Configura, planea preventivos y asigna órdenes' },
  { id: 'tecnico', label: 'Técnico', acceso: 'App móvil, sus órdenes', hace: 'Recibe, ejecuta y cierra órdenes con evidencia' },
  { id: 'solicitante', label: 'Solicitante', acceso: 'Solo solicitudes', hace: 'Reporta fallas y da seguimiento' },
  { id: 'almacen', label: 'Operador de almacén', acceso: 'Inventario y herramientas', hace: 'Controla refacciones y préstamo de herramientas' },
];

export const FASES = [
  'Plan personalizado',
  'Información y configuración',
  'Capacitación por rol',
  'Arranque y acompañamiento',
  'Adopción',
];

// Pendiente → En curso → Hecho → Pendiente, al tocar el estado.
export const ST = { p: 'Pendiente', c: 'En curso', h: 'Hecho' };
export const NEXT = { p: 'c', c: 'h', h: 'p' };

export const ESTANDAR = {
  si: { label: 'Estandarizar todo', desc: 'Mismos catálogos, procesos y preventivos', doc: 'Estandarizada en todas las plantas' },
  no: { label: 'Cada planta con su configuración', desc: 'Cada una conserva su forma de operar', doc: 'Cada planta conserva su configuración' },
};

export const SUPPORT_LINE = 'Soporte técnico: support@mptotal.com · Chat en mptotal.com · Tickets: mptotal.com/formulario-para-levantar-ticket-de-soporte-tecnico';

export const num = (x) => parseInt(x, 10) || 0;
export const text = (x) => String(x ?? '').trim();

export function blankPlan() {
  return {
    cliente: '', fecha: '', csm: '',
    resp: { nombre: '', puesto: '', correo: '' },
    admin: '',
    partida: '', respPartida: {},
    plantas: { total: '', ubic: '', estandar: '', piloto: '' },
    modulos: { preventivo: '', correctivo: '', inventario: '', integraciones: '' },
    usuarios: { admin: '', tecnico: '', solicitante: '', almacen: '' },
    integ: '', notas: '', tareas: {},
  };
}

// Lo que llega de la base puede venir de una versión anterior del checklist:
// se completa con los campos vacíos para que el formulario nunca lea undefined.
export function normalize(data) {
  const base = blankPlan();
  const src = data || {};
  const out = { ...base, ...src };
  for (const key of ['resp', 'plantas', 'modulos', 'usuarios']) {
    out[key] = { ...base[key], ...(src[key] || {}) };
  }
  out.respPartida = src.respPartida || {};
  out.tareas = src.tareas || {};
  return out;
}

// Las tareas no se capturan: salen de lo marcado en el checklist. El id de
// cada una es estable para que su estado sobreviva a cambios en el formulario.
export function buildTasks(p) {
  const m = p.modulos;
  const f1 = (id) => m[id] === 'f1';
  const plantas = num(p.plantas.total);
  const piloto = text(p.plantas.piloto);
  const later = MODULOS.filter((x) => m[x.id] === 'f2').map((x) => x.label.toLowerCase());
  const T = [[], [], [], [], []];

  T[0].push(
    ['k1', 'Kickoff realizado'],
    ['k2', `Responsable de implementación confirmado${text(p.resp.nombre) ? `: ${text(p.resp.nombre)}` : ''}`],
    ['k3', 'Plan de implementación enviado al cliente'],
    ['k4', 'Plan validado por el responsable'],
    ['k5', 'Reunión semanal agendada'],
  );

  T[1].push(['d1', 'Cuenta del administrador registrada']);
  if (PARTIDAS[p.partida]) {
    PARTIDAS[p.partida].tareas.forEach((t, i) => T[1].push([`p-${p.partida}-${i}`, t]));
  } else {
    T[1].push(['p-def', 'Definir el punto de partida']);
  }
  if (p.plantas.estandar === 'si' && plantas > 1) {
    T[1].push(['d2', 'Definir catálogos, procesos y preventivos estándar para todas las plantas']);
  }
  T[1].push(['d3', `Configurar la organización piloto${piloto ? `: ${piloto}` : ''}`], ['d4', 'Cargar el catálogo de activos']);
  if (f1('preventivo')) T[1].push(['d5', 'Cargar los planes de mantenimiento preventivo']);
  if (f1('correctivo')) T[1].push(['d6', 'Configurar solicitudes y órdenes correctivas']);
  if (f1('inventario')) T[1].push(['d7', 'Cargar inventario y herramientas']);
  if (f1('integraciones')) T[1].push(['d8', 'Configurar las integraciones de la fase 1']);
  T[1].push(['d9', 'Dar de alta a los usuarios'], ['d10', 'Carga de prueba validada por el cliente']);

  T[2].push(['c1', 'Capacitación: administrador / planificador']);
  if (f1('preventivo') || f1('correctivo')) T[2].push(['c2', 'Capacitación: técnicos en la app móvil']);
  if (f1('correctivo')) T[2].push(['c3', 'Capacitación: solicitantes']);
  if (f1('inventario')) T[2].push(['c4', 'Capacitación: operador de almacén']);
  T[2].push(['c5', 'Ejercicio práctico integral con sus datos']);

  T[3].push(
    ['a1', 'Arranque de la organización piloto'],
    ['a2', 'Acompañamiento cercano de 2 semanas'],
    ['a3', 'Uso estable confirmado por el responsable'],
  );

  T[4].push(['o1', 'Revisión de resultados con el responsable']);
  if (plantas > 1) T[4].push(['o2', 'Replicar en las demás plantas']);
  if (later.length) T[4].push(['o3', `Planear la siguiente fase: ${later.join(', ')}`]);
  T[4].push(['o4', 'Cierre de la implementación']);

  return T.map((list) => list.map(([id, t]) => ({ id, t, s: p.tareas[id] || 'p' })));
}

export function progress(tasks) {
  const all = tasks.flat();
  const done = all.filter((t) => t.s === 'h').length;
  return { done, total: all.length, pct: all.length ? Math.round((done * 100) / all.length) : 0 };
}

// Todo lo que el documento necesita, ya resuelto: la vista previa y el PDF
// leen de aquí para no poder decir cosas distintas.
export function summarize(p) {
  const tasks = buildTasks(p);
  const partida = PARTIDAS[p.partida] || null;
  const answers = partida
    ? partida.qs
        .map((q, i) => [q, text((p.respPartida[p.partida] || {})[i])])
        .filter(([, a]) => a)
    : [];
  const byChoice = (choice) => MODULOS.filter((m) => p.modulos[m.id] === choice).map((m) => m.label);
  return {
    tasks,
    progress: progress(tasks),
    partida,
    answers,
    fase1: ['Activos y ajustes', ...byChoice('f1')],
    despues: byChoice('f2'),
    noAplica: byChoice('na'),
    porDecidir: MODULOS.filter((m) => !p.modulos[m.id]).map((m) => m.label),
    plantas: num(p.plantas.total),
    estandar: ESTANDAR[p.plantas.estandar]?.doc || '',
    piloto: text(p.plantas.piloto),
    totalUsers: ROLES.reduce((s, r) => s + num(p.usuarios[r.id]), 0),
  };
}

export function fmtPlanDate(day) {
  if (!day) return '';
  try {
    return new Date(`${day}T12:00:00Z`).toLocaleDateString('es-MX', {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
    });
  } catch {
    return day;
  }
}
